import {environment} from 'src/environments/environment';
import {inject, Injectable} from '@angular/core';
import {map, Observable} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {mapParseItem, mapParseList} from '@brunata-angular/util';
import {z} from 'zod';

@Injectable({
    providedIn: 'root',
})
export class UserAdministrationService {
    private http = inject(HttpClient);

    getUsers(): Observable<UserListItem[]> {
        return this.http
            .get(`${environment.onlineServiceUrl}/users`)
            .pipe(mapParseList(userListItemSchema));
    }

    getUser(loginNameId: number): Observable<AdminUser> {
        return this.http
            .get(`${environment.onlineServiceUrl}/users/${loginNameId}`)
            .pipe(mapParseItem(adminUserSchema));
    }

    // The endpoint signals availability via HTTP status: 204 means the username is
    // already taken, 200 means it is free. Both responses have no body.
    checkUsernameExists(username: string): Observable<boolean> {
        return this.http
            .get(`${environment.onlineServiceUrl}/users/checkexists`, {
                params: {username},
                observe: 'response',
            })
            .pipe(map(response => response.status === 204));
    }

    createUser(user: CreateUserRequest): Observable<AdminUser> {
        return this.http
            .post(`${environment.onlineServiceUrl}/users`, user)
            .pipe(mapParseItem(adminUserSchema));
    }

    updateUser(loginNameId: number, user: UpdateUserRequest): Observable<AdminUser> {
        return this.http
            .put(`${environment.onlineServiceUrl}/users/${loginNameId}`, user)
            .pipe(mapParseItem(adminUserSchema));
    }

    deleteUser(loginNameId: number): Observable<void> {
        return this.http.delete<void>(`${environment.onlineServiceUrl}/users/${loginNameId}`);
    }

    resetPassword(loginNameId: number): Observable<void> {
        return this.http.post<void>(
            `${environment.onlineServiceUrl}/users/${loginNameId}/resetpassword`,
            {},
        );
    }

    getUserBuildingAccess(loginNameId: number): Observable<string[]> {
        return this.http.get<string[]>(
            `${environment.onlineServiceUrl}/users/${loginNameId}/buildings`,
        );
    }

    updateUserBuildingAccess(loginNameId: number, buildingNos: string[]): Observable<void> {
        return this.http.put<void>(
            `${environment.onlineServiceUrl}/users/${loginNameId}/buildings`,
            buildingNos,
        );
    }
}

const userListItemSchema = z.object({
    personName: z.string().nullable(),
    loginNameId: z.number(),
    userName: z.string(),
    email: z.string().nullable(),
    role: z.string(),
    note: z.string().nullable(),
});

const adminUserSchema = z.object({
    loginNameId: z.number(),
    userName: z.string(),
    email: z.string().nullable(),
    role: z.string(),
    note: z.string().nullable(),
    personName: z.string().nullable(),
});

export type UserListItem = z.infer<typeof userListItemSchema>;
export type AdminUser = z.infer<typeof adminUserSchema>;

export type CreateUserRequest = {
    personName: string;
    userName: string;
    email: string;
    role: string;
    note: string;
};

export type UpdateUserRequest = {
    personName: string;
    email: string;
    role: string;
    note: string;
};
